import { Modal, Select, Spinner } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Divider } from 'antd'
import { getNominative } from 'common/utils'
import useResidentNeeds, { type ResidentNeedV6 } from 'hooks/useResidentNeeds'
import { NAMESPACE } from 'locale'
import { carePlansConstants } from 'locale/carePlans'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

interface AddNeedsModalProps {
  isModalVisible: boolean
  setIsModalVisible: (visible: boolean) => void
  residentId: string
  linkedNeedIds: string[]
  onConfirm: (selectedIds: string[]) => void
}

const AddNeedsModal: React.FC<AddNeedsModalProps> = ({
  isModalVisible,
  setIsModalVisible,
  residentId,
  linkedNeedIds,
  onConfirm
}) => {
  const { t } = useTranslation(NAMESPACE.CARE_PLANS)
  const preferredCarePlansNominative = useSelector(
    (state: { global: { home: { preferredCarePlansNominative?: string } } }) =>
      state.global.home.preferredCarePlansNominative
  )
  const carePlanNominative = getNominative(
    preferredCarePlansNominative,
    'Care Plan'
  )
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const { needs, isLoading } = useResidentNeeds({ id: residentId })

  useEffect(() => {
    if (isModalVisible) {
      setSelectedIds([])
      setErrorMessage(null)
    }
  }, [isModalVisible])

  const selectOptions = useMemo(() => {
    return needs
      .filter((need: ResidentNeedV6) => !linkedNeedIds.includes(need.id))
      .map((need: ResidentNeedV6) => ({
        key: need.id,
        value: need.id,
        label: need.need.name
      }))
  }, [needs, linkedNeedIds])

  const handleConfirm = () => {
    if (selectedIds.length === 0) {
      setErrorMessage(
        t(carePlansConstants.NEEDS_MODAL_ERROR_EMPTY, { carePlanNominative })
      )
      return
    }
    onConfirm(selectedIds)
    setIsModalVisible(false)
  }

  const handleCancel = () => {
    setSelectedIds([])
    setErrorMessage(null)
  }

  const onSelectChange = (
    _value: string[],
    options: { label?: React.ReactNode; value: string }[]
  ) => {
    setSelectedIds(options.map((option) => option.value))
    setErrorMessage(null)
  }

  return (
    <StyledModal
      noPadding
      isModalVisible={isModalVisible}
      setIsModalVisible={setIsModalVisible}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      closeButton
      closeOnConfirm={false}
      confirmButtonProps={{
        disabled: selectedIds.length === 0 || !!errorMessage,
        variant: 'primary'
      }}
      localisation={{
        title: t(carePlansConstants.NEEDS_MODAL_TITLE),
        confirmText: t(carePlansConstants.NEEDS_MODAL_CONFIRM),
        cancelText: t(carePlansConstants.NEEDS_MODAL_CANCEL)
      }}
    >
      <Divider style={{ margin: '0 0 16px 0' }} />
      <FieldWrapper>
        <FieldLabel weight="medium">
          {t(carePlansConstants.NEEDS_MODAL_FIELD_LABEL)}
        </FieldLabel>
        <HelperText>
          {t(carePlansConstants.NEEDS_MODAL_HELPER_TEXT, {
            carePlanNominative
          })}
        </HelperText>
        {isLoading ? (
          <LoadingContainer>
            <Spinner />
          </LoadingContainer>
        ) : (
          <Select<string[], { label?: React.ReactNode; value: string }>
            placeholder={t(carePlansConstants.NEEDS_MODAL_PLACEHOLDER)}
            style={{ width: '100%' }}
            value={selectedIds}
            mode="multiple"
            onChange={(value, options) =>
              onSelectChange(value as string[], options as [])
            }
            options={selectOptions}
            dropdownStyle={{ maxWidth: '100vw' }}
          />
        )}
        {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
      </FieldWrapper>
      <Divider style={{ margin: '8px 0 0 0' }} />
    </StyledModal>
  )
}

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FieldLabel = styled(Text)`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.neutral.color2};
`

const HelperText = styled(Text)`
  font-size: 13px;
  color: ${({ theme }) => theme.color.neutral.color4};
  margin-bottom: 8px;
`

const ErrorText = styled.span`
  font-size: 15px !important;
  color: ${({ theme }) => theme.color.status.danger};
  margin-top: 4px;
  display: block;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 40px;
`

const StyledModal = styled(Modal)`
  min-width: 600px;

  .ant-modal-content {
    padding-top: 20px !important;
    padding-left: 24px !important;
    padding-right: 24px !important;
  }

  .ant-modal-header {
    padding-bottom: 4px !important;
  }

  .ant-modal-title {
    font-size: 22px !important;
    font-weight: 500 !important;
    text-transform: none !important;
    color: ${({ theme }) => theme.color.neutral.color3} !important;
  }

  .ant-select {
    min-height: 40px !important;
    height: auto !important;
  }

  .ant-select-selector {
    min-height: 40px !important;
    height: auto !important;
  }

  .ant-modal-footer {
    margin-top: 0 !important;
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }
`

export default AddNeedsModal
